/** Полка, история чтения и выписки — три вкладки одной страницы. */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, coverUrl, SHELF_LABELS } from '../api'
import type { Bookmark, ShelfBook, ShelfStatus } from '../api'
import { Spinner } from '../components/Spinner'

type Tab = 'reading' | 'want' | 'finished' | 'favorite' | 'history' | 'quotes'

const TABS: { key: Tab; label: string }[] = [
  { key: 'reading', label: 'Читаю' },
  { key: 'want', label: 'Хочу прочесть' },
  { key: 'finished', label: 'Прочитано' },
  { key: 'favorite', label: 'Избранное' },
  { key: 'history', label: 'История' },
  { key: 'quotes', label: 'Выписки' },
]

export function ShelfPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab) || 'reading'

  const [books, setBooks] = useState<ShelfBook[]>([])
  const [quotes, setQuotes] = useState<Bookmark[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      if (tab === 'quotes') {
        setQuotes((await api.bookmarks({ limit: 200 })).items)
      } else if (tab === 'history') {
        setBooks((await api.history({ limit: 100 })).items)
      } else if (tab === 'favorite') {
        setBooks((await api.shelf({ favorite: true, limit: 100 })).items)
      } else {
        setBooks((await api.shelf({ status: tab, limit: 100 })).items)
      }
    } catch {
      setError('не удалось загрузить')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void load()
  }, [load])

  const removeQuote = async (markId: string) => {
    await api.removeBookmark(markId)
    setQuotes((prev) => prev.filter((q) => q.uuid !== markId))
  }

  return (
    <>
      <div className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate('/')}>
          ← Каталог
        </button>
        <span className="topbar__brand">Моя полка</span>
        <span className="topbar__spacer" />
      </div>

      <div className="tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            className={`btn${tab === item.key ? ' btn--primary' : ''}`}
            onClick={() => setParams({ tab: item.key })}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="content">
        {error && <p className="error">{error}</p>}
        {loading && <Spinner />}

        {!loading && tab === 'quotes' && (
          <QuoteList quotes={quotes} onRemove={removeQuote} />
        )}
        {!loading && tab !== 'quotes' && <ShelfList books={books} showRead={tab === 'history'} />}
      </div>
    </>
  )
}

function ShelfList({ books, showRead }: { books: ShelfBook[]; showRead: boolean }) {
  if (books.length === 0) {
    return <p className="dim">Пусто. Отметьте книгу в её карточке.</p>
  }
  return (
    <div className="shelf-list">
      {books.map((book) => (
        <Link key={book.id} className="shelf-item" to={`/book/${book.id}`}>
          <div className="shelf-item__cover">
            {book.has_cover ? (
              <img src={coverUrl(book.id)} alt="" loading="lazy" />
            ) : (
              <div className="card__placeholder">{book.title}</div>
            )}
          </div>
          <div className="shelf-item__body">
            <div className="shelf-item__title">{book.title}</div>
            <div className="dim">{book.authors.map((a) => a.name).join(', ')}</div>
            <div className="dim">
              {book.favorite && '★ '}
              {book.status !== 'none' && SHELF_LABELS[book.status as ShelfStatus]}
              {book.progress > 0 && ` · прочитано ${Math.round(book.progress * 100)} %`}
              {showRead && book.read_at > 0 && ` · ${formatDate(book.read_at)}`}
            </div>
            {book.progress > 0 && (
              <div className="progress">
                <div className="progress__bar" style={{ width: `${book.progress * 100}%` }} />
              </div>
            )}
          </div>
        </Link>
      ))}
    </div>
  )
}

function QuoteList({
  quotes,
  onRemove,
}: {
  quotes: Bookmark[]
  onRemove: (id: string) => void
}) {
  if (quotes.length === 0) {
    return <p className="dim">Выписок пока нет. Их можно делать в читалке.</p>
  }
  return (
    <div className="quotes">
      {quotes.map((quote) => (
        <div key={quote.uuid} className="quote">
          <div className="quote__head">
            <Link to={`/read/${quote.book_id}`}>{quote.book_title || 'Книга'}</Link>
            <span className="topbar__spacer" />
            <span className="dim">{formatDate(quote.created_at)}</span>
            <button
              className="btn btn--ghost"
              title="Удалить"
              onClick={() => void onRemove(quote.uuid)}
            >
              ×
            </button>
          </div>
          {quote.excerpt && <blockquote>{quote.excerpt}</blockquote>}
          {quote.note && <div className="quote__note">{quote.note}</div>}
        </div>
      ))}
    </div>
  )
}

/** Отметка времени приходит в секундах — в JavaScript нужны миллисекунды. */
function formatDate(seconds: number): string {
  if (!seconds) return ''
  return new Date(seconds * 1000).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
